import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Users,
  Phone,
  Video,
  MessageSquare,
  User,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';

// Team Members = real tenant staff roster joined with live WebRTC presence.
// `teamMembers`: [{ id, staffId, name, role, status: 'online'|'offline', isSelf }]
// id = signaling/presence id (Firebase uid). Never renders a raw uid as a name.
const OnlineUsersPanel = ({
  teamMembers = [],
  isConnected,
  onInitiateCall,
  currentCall,
  onRefresh,
  onInitiateConversation,
  unreadByPeer = {},
  isLoading = false,
  error = false
}) => {
  const { t } = useLanguage();
  const [callingUser, setCallingUser] = useState(null);
  // Inline, non-blocking call-start error (replaces the blocking alert). initiateCall now returns a
  // result object; a thrown error is still handled defensively. Device-aware messages come from the hook.
  const [inlineError, setInlineError] = useState(null);

  const handleCall = async (member, callType) => {
    if (currentCall || callingUser) return;
    setInlineError(null);
    setCallingUser(member.id);
    try {
      const res = await onInitiateCall(member.id, callType);
      if (res && res.ok === false && !res.suppressed) {
        setInlineError(res.message || 'Could not start the call.');
      }
    } catch (error) {
      // Only surface a genuine failure — never a raw stack or device id.
      setInlineError(error?.userMessage || error?.message || 'Could not start the call.');
    } finally {
      setCallingUser(null);
    }
  };

  return (
    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>{t('tc.teamMembers')}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Badge className={`${isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {isConnected ? (<><Wifi className="h-3 w-3 mr-1" />Connected</>) : (<><WifiOff className="h-3 w-3 mr-1" />Disconnected</>)}
            </Badge>
            <Button variant="ghost" size="sm" onClick={onRefresh} disabled={isLoading} title={t('tc.refreshStaffPresence')}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && teamMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="h-10 w-10 mx-auto mb-3 text-gray-400 animate-spin" />
            <p>{t('tc.loadingStaff')}</p>
          </div>
        ) : error && teamMembers.length === 0 ? (
          <div className="text-center py-8 text-red-600">
            <WifiOff className="h-10 w-10 mx-auto mb-3 text-red-400" />
            <p>{t('tc.loadStaffError')}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={onRefresh}>{t('common.retry')}</Button>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{t('tc.noStaffYet')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {teamMembers.map((member) => {
              const online = member.status === 'online';
              const callable = online && !member.isSelf;
              return (
                <div
                  key={`tm-${member.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${online ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {member.name}{member.isSelf ? ` ${t('tc.you')}` : ''}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {member.role ? `${member.role} · ` : ''}
                        <span className={online ? 'text-green-600' : 'text-gray-400'}>{online ? 'Online' : 'Offline'}</span>
                      </p>
                    </div>
                  </div>

                  {member.isSelf ? (
                    <Badge variant="secondary" className="text-xs">{t('tc.thisIsYou')}</Badge>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleCall(member, 'voice')}
                        disabled={!callable || !!currentCall || callingUser === member.id}
                        title={callable ? t('tc.voiceCall') : t('common.offline')}
                        className="hover:bg-blue-50"
                      >
                        {callingUser === member.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleCall(member, 'video')}
                        disabled={!callable || !!currentCall || callingUser === member.id}
                        title={callable ? t('tc.videoCall') : t('common.offline')}
                        className="hover:bg-green-50"
                      >
                        {callingUser === member.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                      </Button>
                      {/* Staff-to-staff chat — real, persisted staff DM (/api/v1/staff-chat/*).
                          Never routes a staff member into the customer ticket flow. The unread
                          badge lets the recipient see a new message WITHOUT already having the
                          chat modal open (fed by the global staff-chat conversations poll). */}
                      {(() => {
                        const unread = unreadByPeer[member.id] || unreadByPeer[member.staffId] || 0;
                        return (
                          <div className="relative">
                            <Button
                              size="sm" variant="outline"
                              className="hover:bg-purple-50"
                              onClick={() => onInitiateConversation && onInitiateConversation({ id: member.id, name: member.name, role: member.role, staff: true })}
                              title={unread > 0 ? t('tc.chatUnread', { count: unread }) : t('tc.chat')}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                            {unread > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center font-semibold">
                                {unread > 9 ? '9+' : unread}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {inlineError && (
          <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start justify-between">
            <span>{inlineError}</span>
            <button className="ml-2 text-amber-500 hover:text-amber-700" onClick={() => setInlineError(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        {!isConnected && (
          <div className="mt-3 text-xs text-gray-500 text-center">
            {t('tc.presenceReconnecting')}
          </div>
        )}

        {currentCall && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-blue-800 font-medium">
                {currentCall.type === 'video' ? t('tc.videoCallInProgress') : t('tc.voiceCallInProgress')}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnlineUsersPanel;
